// The Whole Story – curved ribbon marquee: text runs infinitely along the SVG path.
// The text is the item list repeated N times; we move startOffset left by one repeat
// width and wrap around, so the loop is seamless. Runs only while the ribbon is on screen.
if (!customElements.get('twst-ribbon')) {
  customElements.define(
    'twst-ribbon',
    class TwstRibbon extends HTMLElement {
      connectedCallback() {
        // note: don't use `this.dir` – it is the native HTML text-direction property
        this.speed = Number(this.dataset.speed) || 0;
        this.sign = this.dataset.direction === 'right' ? 1 : -1;
        this.still =
          this.hasAttribute('data-static') ||
          !this.speed ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.paths = [...this.querySelectorAll('textPath')];
        this.distance = 0;
        this.units = new Map();
        this.tick = this.tick.bind(this);
        this.reset = () => {
          this.units.clear();
          this.center();
        };

        this.center();
        window.addEventListener('resize', this.reset, { passive: true });
        document.fonts?.ready.then(this.reset);
        document.fonts?.addEventListener?.('loadingdone', this.reset);

        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            if (this.visible) {
              this.classList.add('is-in');
              this.play();
            }
          },
          { rootMargin: '150px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        window.removeEventListener('resize', this.reset);
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      play() {
        if (this.still || this.frame) return;
        this.last = null;
        this.frame = requestAnimationFrame(this.tick);
      }

      // Vertically centre the capitals on the ribbon's centre line.
      // The text baseline sits on the path; shift it down by half the font's real cap height
      // (measured from the loaded font), plus the optional offset from the section settings.
      // Note: a `dy` attribute can't be used – SVG applies it to the first glyph, which in a
      // marquee sits before the path start (not rendered), so browsers drop the shift.
      // A transform on the <text> moves the whole run instead (the curve is gentle, <15°).
      center() {
        const ctx = (TwstRibbon.canvas ||= document.createElement('canvas')).getContext('2d');
        const extra = Number(this.dataset.offset || 0) / 100;
        this.querySelectorAll('.twst-ribbon__text').forEach((text) => {
          const cs = getComputedStyle(text);
          const size = parseFloat(text.getAttribute('font-size')) || parseFloat(cs.fontSize) || 40;
          ctx.font = `${cs.fontStyle} ${cs.fontWeight} 100px ${cs.fontFamily}`;
          const m = ctx.measureText('HEXOD');
          const ascent = m.actualBoundingBoxAscent || 70;
          const descent = m.actualBoundingBoxDescent || 0;
          const shift = ((ascent - descent) / 2 / 100 + extra) * size;
          text.removeAttribute('dy');
          text.setAttribute('transform', `translate(0 ${shift.toFixed(2)})`);
        });
      }

      // Width of one repeat of the item list, measured on the path that is currently shown
      unitFor(tp) {
        if (!this.units.has(tp)) {
          const shown = tp.getClientRects().length > 0;
          const len = shown ? tp.parentNode.getComputedTextLength() : 0;
          if (!shown || !len) return 0; // hidden breakpoint or not laid out yet – try again next frame
          let repeat = Number(tp.dataset.repeat || 1);
          const unit = len / repeat;
          // make sure the text always covers the whole path while it slides (no gaps)
          const path = document.getElementById((tp.getAttribute('href') || '').slice(1));
          const pathLen = path ? path.getTotalLength() : 0;
          if (unit > 0 && (repeat - 1) * unit < pathLen + unit) {
            const unitText = tp.textContent.slice(0, tp.textContent.length / repeat);
            while ((repeat - 1) * unit < pathLen + unit && repeat < 60) {
              tp.textContent += unitText;
              repeat += 1;
            }
            tp.dataset.repeat = repeat;
          }
          this.units.set(tp, unit);
        }
        return this.units.get(tp);
      }

      tick(now) {
        if (!this.visible || !this.isConnected) {
          this.frame = null;
          return;
        }
        if (this.last == null) this.last = now;
        const dt = Math.min(Math.max(now - this.last, 0), 100) / 1000;
        this.last = now;
        this.distance += this.speed * dt;

        this.paths.forEach((tp) => {
          const unit = this.unitFor(tp);
          if (!unit) return;
          const d = this.distance % unit; // 0 … unit
          // left: 0 → -unit, right: -unit → 0 (then wraps)
          const offset = this.sign < 0 ? -d : d - unit;
          tp.setAttribute('startOffset', offset.toFixed(2));
        });

        this.frame = requestAnimationFrame(this.tick);
      }
    }
  );
}
